import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TODAY = format(new Date(), "yyyy-MM-dd");

export function BookingModal({ provider, open, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", phone: f.phone || user.phone || "" })); }, [user]);

  const valid = form.name.trim().length >= 2 && form.phone.trim().length >= 10 && !!form.date && form.reason.trim().length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please sign in to request your appointment"); nav("/login", { state: { from: `/provider/${provider.id}?book=1` } }); return; }
    if (!valid) return;
    setBusy(true);
    try {
      await api.post("/appointment-requests", { provider_id: provider.id, name: form.name.trim(), phone: form.phone.trim(), date: form.date, reason: form.reason.trim() });
      setDone(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const reset = () => { setDone(false); setForm({ name: "", phone: "", date: "", reason: "" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="booking-modal">
        <div className="navy text-white px-6 py-5 relative grain">
          <DialogHeader><DialogTitle className="font-display text-lg">Book with {provider?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-sky-200 mt-1">{provider?.area}{provider?.city ? `, ${provider.city}` : ""}</div>
        </div>
        <div className="p-6">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6" data-testid="booking-success">
                <div className="h-16 w-16 rounded-full bg-emerald-500 text-white grid place-items-center mx-auto mb-4"><CheckCircle2 size={30} /></div>
                <h3 className="font-display text-xl font-bold text-[#06152D]">Request received!</h3>
                <p className="text-sm text-[#475569] mt-2 max-w-xs mx-auto">Your appointment request has been received. We will contact you shortly.</p>
                <Button onClick={reset} className="rounded-full mt-6 bg-[#06152D] hover:bg-[#0284C7]" data-testid="booking-done-button">Done</Button>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="booking-form">
                <Field label="Name"><Input data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="h-11" /></Field>
                <Field label="Phone Number"><Input data-testid="booking-phone-input" inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "").slice(0, 10) })} placeholder="10-digit mobile number" className="h-11" /></Field>
                <Field label="Preferred Date"><Input data-testid="booking-date-input" type="date" min={TODAY} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11" /></Field>
                <Field label="Reason for Visit"><Textarea data-testid="booking-reason-input" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Briefly describe your concern" /></Field>
                <Button type="submit" disabled={busy || !valid} data-testid="booking-modal-confirm-button" className="w-full h-12 rounded-full bg-[#0284C7] hover:bg-[#06152D] font-semibold">
                  {busy ? "Submitting…" : user ? "Request Appointment" : "Sign in & request"}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-semibold text-[#475569] mb-1 block">{label}</span>{children}</label>
);
